import {
  Component,
  ViewChild,
  WritableSignal,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpParams, httpResource } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter } from '@angular/material/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, ParamMap, RouterModule, Router } from '@angular/router';
import { LeafletGroupComponent } from '@components/leaflet/group/group.component';
import { ListComponent } from '@components/list/list.component';
import { environment } from '@environment';
import {
  addProjection,
  Chip,
  GeoJSONToWKT,
  ItemListResponse,
  PlanktonEvent,
  StringArrayChip,
  StringChip,
  WKTChip,
  WKTToGeoJSON,
} from '@models';
import { PlanktonEventAbstractPipe, PlanktonEventTitlePipe } from '@pipes';
import { EventFilterComponent } from '../filter/filter.component';
import geojson from 'geojson';

@Component({
  selector: 'app-plankton-event-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  imports: [
    CommonModule,
    LeafletGroupComponent,
    ListComponent,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatPaginatorModule,
    MatTooltipModule,
    PlanktonEventAbstractPipe,
    PlanktonEventTitlePipe,
    RouterModule,
  ],
})
export class PlanktonEventListComponent {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  dialog = inject(MatDialog);
  route = inject(ActivatedRoute);
  router = inject(Router);

  planktonDatasetId = environment.planktonDatasetId;

  count = signal(0);
  page = signal(0);
  pageSize = signal(10);
  q = signal('');

  location = signal<string | undefined>(undefined);
  geojsonData = signal<geojson.FeatureCollection | undefined>(undefined);

  singleFilters = new Map<string, WritableSignal<string>>([
    ['fieldNumber', signal('')],
    ['from', signal('')],
    ['originID', signal('')],
    ['planktonType', signal('')],
    ['q', this.q],
    ['until', signal('')],
  ]);

  multiFilters = new Map<string, WritableSignal<string[]>>([
    ['expedition', signal<string[]>([])],
    ['gearType', signal<string[]>([])],
  ]);

  httpParams = computed(() => {
    const count = untracked(() => this.count() === 0 || this.page() === 0);
    let params = new HttpParams()
      .set('count', count)
      .set('occurrenceCount', true)
      .set('skip', this.page() * this.pageSize())
      .set('take', this.pageSize());
    for (const [k, sig] of this.singleFilters) {
      if (sig() != '') {
        params = params.set(k, sig());
      }
    }
    for (const [k, sig] of this.multiFilters) {
      for (const vv of sig()) {
        params = params.append(k, vv);
      }
    }
    const location = this.location();
    if (location) {
      params = params.set('location', location);
    }

    return params;
  });

  response = httpResource<ItemListResponse<PlanktonEvent>>(() => {
    return `${environment.kinkoHost}/plankton/event/?${this.httpParams()}`;
  });

  activeChips = computed(() => {
    const p = this.httpParams();
    const chips: Chip[] = [];

    for (const [k, sig] of this.singleFilters) {
      if (p.has(k)) {
        chips.push(new StringChip(k, sig));
      }
    }
    for (const [k, sig] of this.multiFilters) {
      const v = p.getAll(k);
      if (v) {
        for (const vv of v) {
          chips.push(new StringArrayChip(k, sig, vv));
        }
      }
    }
    if (this.location()) {
      chips.push(new WKTChip('location', this.location));
    }
    return chips.sort((one, other) => one.compare(other));
  });

  csvDownloadURL = computed(() => {
    const q = this.httpParams().delete('skip').delete('take').delete('count').set('format', 'csv');
    return `${environment.kinkoHost}/plankton/event/?${q}`;
  });

  geoJSONURL = computed(() => {
    const urlPrefix = window.location.protocol + '//' + window.location.host + '/plankton/event/';
    const q = this.httpParams()
      .delete('skip')
      .delete('take')
      .delete('count')
      .set('format', 'geojson')
      .set('urlPrefix', urlPrefix);
    return `${environment.kinkoHost}/plankton/event/?${q}`;
  });

  geoJSONResponse = httpResource<geojson.FeatureCollection>(() => {
    // event positions for leaflet. Only fetched, when there is at least one
    // expedition filter to avoid fetching a geoJSON with several thousands of
    // points

    if (this.multiFilters.get('expedition')!().length === 0) {
      return undefined;
    }
    return this.geoJSONURL();
  });

  constructor() {
    this.route.queryParamMap.subscribe((pm: ParamMap) => {
      this.page.set(parseInt(pm.get('page') || '0'));
      this.pageSize.set(parseInt(pm.get('pageSize') || '10'));
      for (const [k, sig] of this.singleFilters) {
        sig.set(pm.get(k) || '');
      }
      for (const [k, sig] of this.multiFilters) {
        sig.set([...(pm.getAll(k) || [])]);
      }
      this.location.set(pm.get('location') ?? undefined);
    });

    effect(() => {
      if (!this.response.hasValue() || !this.response.value().count) {
        return;
      }

      this.count.set(this.response.value().count);
    });

    effect(() => {
      // merge features from location filter and event positions into same map.

      const geojson = WKTToGeoJSON(this.location());

      if (this.geoJSONResponse.hasValue()) {
        const eventPositions = this.geoJSONResponse.value().features.map(addProjection);
        const features = [...geojson.features, ...eventPositions];
        this.geojsonData.set({ ...geojson, features: features });
      } else {
        this.geojsonData.set(geojson);
      }
    });
  }

  onPageEvent(event: PageEvent) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        page: event.pageIndex || undefined,
        pageSize: event.pageSize === 10 ? undefined : event.pageSize,
      },
      replaceUrl: true,
    });
  }

  onSearchEvent(event: Event) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        q: (event.target as HTMLInputElement).value.trim() || undefined,
        page: undefined,
      },
      replaceUrl: true,
    });
  }

  onFilterRemove(chip: Chip) {
    chip.remove();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        [chip.key]: chip.signal() || undefined,
        page: undefined,
      },
    });
  }

  openFilterDialog() {
    const data: Record<string, string | string[] | geojson.FeatureCollection> = {};
    for (const [k, sig] of this.singleFilters) {
      data[k] = sig();
    }
    for (const [k, sig] of this.multiFilters) {
      data[k] = [...sig()];
    }
    data['geojson'] = WKTToGeoJSON(this.location());

    this.dialog
      .open(EventFilterComponent, {
        data: data,
        minWidth: '360px',
        maxWidth: '960px',
      })
      .afterClosed()
      .subscribe(
        (
          data: {
            expedition: string[];
            fieldNumber: string;
            from: Date | null;
            gearType: string[];
            geojson: GeoJSON.FeatureCollection | undefined;
            planktonType: string;
            q: string;
            until: Date | null;
          } | null,
        ) => {
          if (!data) {
            return;
          }

          const qp: Record<string, string | string[] | undefined> = {
            expedition: data.expedition || undefined,
            fieldNumber: data.fieldNumber,
            from: data.from ? this.dateAdapter.format(data.from, null) : undefined,
            gearType: data.gearType || undefined,
            location: data.geojson?.features?.length ? GeoJSONToWKT(data.geojson) : undefined,
            page: undefined,
            planktonType: data.planktonType,
            q: data.q,
            until: data.until ? this.dateAdapter.format(data.until, null) : undefined,
          };

          this.router.navigate([], {
            relativeTo: this.route,
            queryParamsHandling: 'merge',
            queryParams: qp,
            replaceUrl: true,
          });
        },
      );
  }
}
